import _ from 'lodash';
import { SgSubmissionUtil } from 'saga-modules';
import SmiConstants from 'sg-smi-constants';
import { LoadSaveService } from 'gw-capability-quoteandbind';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import moment from 'moment';

const updateDataOnValueChange = (modelVM, path, newValue, updateData, updateDraftDataAndRequote) => {
    const modelCopy = _.clone(modelVM);
    const previousValue = _.get(modelVM, `${path}.value`);
    _.set(modelCopy, path, newValue);
    if (previousValue === undefined && newValue === false) {
        updateData(modelCopy);
    } else {
        updateDraftDataAndRequote(modelCopy, SmiConstants.DIALLER_JOURNEY_INDEX.COVER_ADJUST);
    }
};

const handleAddonChangeWithModal = (props) => {
    const {
        path,
        newValue,
        modelVM,
        openModal,
        updateData,
        updateDraftDataAndRequote
    } = props;
    const previousValue = _.get(modelVM, `${path}.value`);
    if (newValue && previousValue !== newValue) {
        // for validation purposes
        _.set(modelVM, path, true);
        openModal(modelVM).then((resultModelVM) => {
            const modelCopy = _.clone(resultModelVM);
            _.set(modelCopy, path, true);
            updateDraftDataAndRequote(modelCopy, SmiConstants.DIALLER_JOURNEY_INDEX.COVER_ADJUST);
        }, (resultModelVM) => {
            const modelCopy = _.clone(resultModelVM);
            _.set(modelCopy, path, false);
            updateDraftDataAndRequote(modelCopy, SmiConstants.DIALLER_JOURNEY_INDEX.COVER_ADJUST);
        });
    }
    if (newValue === false && previousValue !== newValue) {
        updateDataOnValueChange(modelVM, path, newValue, updateData, updateDraftDataAndRequote);
    }
};

const getMonthlyPriceByCoverLevel = (modelVM, coverLevel) => {
    const monthlyPP = SgSubmissionUtil.getMonthlyPaymentPlan(modelVM);
    switch (coverLevel) {
        case SmiConstants.COVER_LEVEL_CODES.SUPER:
            return monthlyPP.lobData.privateMedical.superAmount;
        case SmiConstants.COVER_LEVEL_CODES.SECURE:
            return monthlyPP.lobData.privateMedical.secureAmount;
        case SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS:
            return monthlyPP.lobData.privateMedical.saverPlusAmount;
        default:
            return undefined;
    }
};

const getPaymentPlanPriceByCoverLevel = (modelVM, coverLevel, billingId) => {
    const paymentPlan = SgSubmissionUtil.getPaymentPlanByBillingId(modelVM, billingId);
    switch (coverLevel) {
        case SmiConstants.COVER_LEVEL_CODES.SUPER:
            return paymentPlan.lobData.privateMedical.superAmount;
        case SmiConstants.COVER_LEVEL_CODES.SECURE:
            return paymentPlan.lobData.privateMedical.secureAmount;
        case SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS:
            return paymentPlan.lobData.privateMedical.saverPlusAmount;
        default:
            return undefined;
    }
};

const getQuarterlyPriceByCoverLevel = (modelVM, coverLevel) => {
    const quarterlyPP = SgSubmissionUtil.getQuarterlyPaymentPlan(modelVM);
    if (!quarterlyPP) {
        return undefined;
    }
    switch (coverLevel) {
        case SmiConstants.COVER_LEVEL_CODES.SUPER:
            return quarterlyPP.lobData.privateMedical.superAmount;
        case SmiConstants.COVER_LEVEL_CODES.SECURE:
            return quarterlyPP.lobData.privateMedical.secureAmount;
        case SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS:
            return quarterlyPP.lobData.privateMedical.saverPlusAmount;
        default:
            return undefined;
    }
};

const getAnnualPriceByCoverLevel = (modelVM, coverLevel) => {
    const annualPP = SgSubmissionUtil.getAnnualPaymentPlan(modelVM);
    switch (coverLevel) {
        case SmiConstants.COVER_LEVEL_CODES.SUPER:
            return annualPP.lobData.privateMedical.superAmount;
        case SmiConstants.COVER_LEVEL_CODES.SECURE:
            return annualPP.lobData.privateMedical.secureAmount;
        case SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS:
            return annualPP.lobData.privateMedical.saverPlusAmount;
        default:
            return undefined;
    }
};

const getMonthsFreeBillingId = (modelVM) => {
    return modelVM.lobData.privateMedical.coverables.smiInsurance.monthsFreeBillingId.value;
};

const getPriceForCoverLevel = (modelVM, coverLevel) => {
    const isMonthlySelected = SgSubmissionUtil.isMonthlyPlanSelected(modelVM);
    const isQuarterlySelected = SgSubmissionUtil.isQuarterlyPlanSelected(modelVM);
    if (isQuarterlySelected) {
        return getQuarterlyPriceByCoverLevel(modelVM, coverLevel);
    }
    if (isMonthlySelected) {
        const monthsFreeBillingId = getMonthsFreeBillingId(modelVM);
        return monthsFreeBillingId ? getPaymentPlanPriceByCoverLevel(modelVM, coverLevel, monthsFreeBillingId)
            : getMonthlyPriceByCoverLevel(modelVM, coverLevel);
    }
    return getAnnualPriceByCoverLevel(modelVM, coverLevel);
};

const getTotalPayableByPaymentPlan = (paymentPlan) => {
    return paymentPlan && paymentPlan.total && paymentPlan.total.amount;
};

const getMonthlyTotalPayable = (modelVM) => {
    const monthlyPP = SgSubmissionUtil.getMonthlyPaymentPlan(modelVM);
    return monthlyPP.total.amount;
};

const getQuarterlyTotalPayable = (modelVM) => {
    const quarterlyPP = SgSubmissionUtil.getQuarterlyPaymentPlan(modelVM);
    return _.get(quarterlyPP, 'total.amount');
};

const getSelectedCoverLevel = (modelVM) => {
    return modelVM.lobData.privateMedical.coverables.smiInsurance.selectedCoverLevel.value.code;
};

const getFrequencyMessage = (modelVM, messages) => {
    if (SgSubmissionUtil.isMonthlyPlanSelected(modelVM)) {
        return messages.PerMonth;
    }
    return SgSubmissionUtil.isQuarterlyPlanSelected(modelVM) ? messages.PerQuarter : messages.PerAnnum;
};

const isQuarterlyPaymentPlanAvailable = (modelVM) => {
    return modelVM.lobData.privateMedical.coverables.smiInsurance.quarterlyAvailable.value
        || modelVM.lobData.privateMedical.coverables.smiInsurance.hasSeenQuarterly.value;
};

const callDialler = async (modelVM, newIndex) => {
    try {
        if (!_.isNil(modelVM.baseData.smiAccountHolder.homeNumber.value)) {
            const phoneNumber = modelVM.baseData.smiAccountHolder.homeNumber.value;
            await LoadSaveService.callDialler(modelVM.quoteID.value, newIndex, phoneNumber);
        }
    } catch (e) {
        console.error(e);
    }
};

const smiEffectiveDate = (modelVM) => {
    return moment(_.get(modelVM, 'baseData.periodStartDate.value')).isSameOrAfter(appConfig.smiEffectiveDate.startDate, 'day');
};

export default {
    handleAddonChangeWithModal,
    updateDataOnValueChange,
    getSelectedCoverLevel,
    getTotalPayableByPaymentPlan,
    getMonthlyTotalPayable,
    getQuarterlyTotalPayable,
    getPriceForCoverLevel,
    getMonthlyPriceByCoverLevel,
    getQuarterlyPriceByCoverLevel,
    getAnnualPriceByCoverLevel,
    getFrequencyMessage,
    isQuarterlyPaymentPlanAvailable,
    getMonthsFreeBillingId,
    getPaymentPlanPriceByCoverLevel,
    callDialler,
    smiEffectiveDate
};
